import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import SEO from '../components/SEO'
import { useToast } from '../context/ToastContext'
import LoadingSpinner from '../components/LoadingSpinner'
import apiClient from '../services/api'
import defaultServices from '../data/defaultServices'
import defaultDoctors from '../data/defaultDoctors'
import useSiteSettings from '../hooks/useSiteSettings'

const appointmentHeroImage = 'https://res.cloudinary.com/dmiicgsbq/image/upload/v1776968081/u0vxfjrsa7ztuu9qecfe.jpg'

const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say']

const normalizeText = (value = '') =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const serviceKeywords = (serviceTitle = '') => {
  const normalized = normalizeText(serviceTitle)

  if (normalized.includes('general') || normalized.includes('checkup')) {
    return []
  }

  if (normalized.includes('cardio')) {
    return ['cardio', 'cardiology', 'heart']
  }

  if (normalized.includes('pediatric')) {
    return ['pediatric', 'pediatrics', 'child']
  }

  if (normalized.includes('orthopedic') || normalized.includes('orthopedics') || normalized.includes('ortho')) {
    return ['orthopedic', 'orthopedics', 'ortho', 'joint']
  }

  if (normalized.includes('dental') || normalized.includes('dent')) {
    return ['dental', 'dentistry', 'oral']
  }

  return normalized.split(' ').filter(Boolean)
}

const matchDoctorsToService = (serviceTitle, doctors = []) => {
  const keywords = serviceKeywords(serviceTitle)

  if (keywords.length === 0) {
    return doctors
  }

  const matches = doctors.filter((doctor) => {
    const specialty = normalizeText(doctor.specialty)
    return keywords.some((keyword) => specialty.includes(keyword) || keyword.includes(specialty))
  })

  return matches.length > 0 ? matches : doctors
}

const AppointmentPage = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const serviceFromQuery = queryParams.get('service') || 'General Checkup'
  const settings = useSiteSettings()
  const { addToast } = useToast()

  const [availableServices, setAvailableServices] = useState(defaultServices)
  const [availableDoctors, setAvailableDoctors] = useState(defaultDoctors)
  const [loading, setLoading] = useState(false)
  const [bootstrapping, setBootstrapping] = useState(true)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    name: '',
    age: '',
    gender: 'Male',
    email: '',
    phone: '',
    address: '',
    service: serviceFromQuery,
    doctorPreference: 'any',
    doctorName: '',
    doctorSpecialty: '',
    date: '',
    time: '',
    message: ''
  })

  const mergeServices = (apiServices = []) => {
    const activeServices = apiServices.filter((service) => service.isActive)
    const normalizedActiveTitles = new Set(activeServices.map((service) => normalizeText(service.title)))

    const missingDefaults = defaultServices.filter(
      (service) => !normalizedActiveTitles.has(normalizeText(service.title))
    )

    return [...activeServices, ...missingDefaults]
  }

  const mergeDoctors = (apiDoctors = []) => {
    const activeDoctors = apiDoctors.filter((doctor) => doctor.active)
    const normalizedDoctorNames = new Set(activeDoctors.map((doctor) => normalizeText(doctor.name)))

    const missingDefaults = defaultDoctors.filter(
      (doctor) => !normalizedDoctorNames.has(normalizeText(doctor.name))
    )

    return [...activeDoctors, ...missingDefaults]
  }

  const loadAppointmentDependencies = useCallback(async () => {
    try {
      const [servicesResponse, doctorsResponse] = await Promise.all([
        apiClient.get('/api/services'),
        apiClient.get('/api/cms/doctors')
      ])

      setAvailableServices(mergeServices(servicesResponse.data))
      setAvailableDoctors(mergeDoctors(doctorsResponse.data))
    } catch (err) {
      console.error('Error fetching appointment form data:', err)
      setAvailableServices(defaultServices)
      setAvailableDoctors(defaultDoctors)
    } finally {
      setBootstrapping(false)
    }
  }, [])

  useEffect(() => {
    loadAppointmentDependencies()
  }, [loadAppointmentDependencies])

  useEffect(() => {
    const hasMatchingService = availableServices.some((service) => service.title === serviceFromQuery)

    setForm((prev) => ({
      ...prev,
      service: hasMatchingService ? serviceFromQuery : prev.service || availableServices[0]?.title || 'General Checkup'
    }))
  }, [serviceFromQuery, availableServices])

  const matchedDoctors = useMemo(
    () => matchDoctorsToService(form.service, availableDoctors),
    [form.service, availableDoctors]
  )

  useEffect(() => {
    if (form.doctorPreference !== 'specific') {
      return
    }

    const selectedDoctorStillVisible = matchedDoctors.some((doctor) => doctor.name === form.doctorName)

    if (!selectedDoctorStillVisible) {
      setForm((prev) => ({
        ...prev,
        doctorPreference: 'any',
        doctorName: '',
        doctorSpecialty: ''
      }))
    }
  }, [form.doctorName, form.doctorPreference, matchedDoctors])

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const selectDoctor = (doctor) => {
    if (!doctor) {
      setForm((prev) => ({
        ...prev,
        doctorPreference: 'any',
        doctorName: '',
        doctorSpecialty: ''
      }))
      return
    }

    setForm((prev) => ({
      ...prev,
      doctorPreference: 'specific',
      doctorName: doctor.name,
      doctorSpecialty: doctor.specialty
    }))
  }

  const resetForm = () => {
    setForm({
      name: '',
      age: '',
      gender: 'Male',
      email: '',
      phone: '',
      address: '',
      service: availableServices.some((service) => service.title === serviceFromQuery)
        ? serviceFromQuery
        : availableServices[0]?.title || 'General Checkup',
      doctorPreference: 'any',
      doctorName: '',
      doctorSpecialty: '',
      date: '',
      time: '',
      message: ''
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setSuccess('')
    setError('')

    try {
      const response = await apiClient.post('/api/appointments', {
        name: form.name,
        age: Number(form.age),
        gender: form.gender,
        email: form.email,
        phone: form.phone,
        address: form.address,
        service: form.service,
        doctorPreference: form.doctorPreference,
        doctorName: form.doctorPreference === 'specific' ? form.doctorName : '',
        doctorSpecialty: form.doctorPreference === 'specific' ? form.doctorSpecialty : '',
        date: form.date,
        time: form.time,
        message: form.message
      })

      if (response.status === 201) {
        const successMessage = 'Appointment request submitted successfully. Your appointment number will be assigned after admin confirmation.'
        setSuccess(successMessage)
        addToast(successMessage, 'success')
        resetForm()
      } else {
        setError('Unexpected response from server.')
        addToast('Unexpected response from server.', 'warning')
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to submit appointment request.'
      setError(message)
      addToast(message, 'danger')
    } finally {
      setLoading(false)
    }
  }

  if (bootstrapping) {
    return <LoadingSpinner size="2rem" />
  }

  return (
    <div className="container py-5 page-grid-gap themed-page themed-appointment-page">
      <SEO
        title="Book Appointment"
        description="Easily book an appointment with our doctors. Select your preferred service, doctor, date, and time online."
        keywords="book appointment, choose doctor, online booking, medical appointment, clinic reservation"
        url="https://harmonymedical.com/appointment"
      />

      <section className="page-hero themed-page-hero themed-page-hero-compact split-page-hero split-page-hero-appointment">
        <div className="split-page-hero-layout">
          <div className="split-page-hero-copy">
            <span className="hero-kicker">Simple Scheduling</span>
            <h1 className="mb-3">Book Appointment</h1>
            <p className="text-muted mb-0">
              Choose the service you need, request a specific doctor if you want one, and share the key patient details our team needs to review and confirm the visit properly.
            </p>
          </div>
          <div className="split-page-hero-visual" aria-hidden="true">
            <div className="split-page-hero-image-shell split-page-hero-image-shell-compact">
              <img
                src={appointmentHeroImage}
                alt="Doctor consulting with a patient"
                className="split-page-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider-wave"></div>

      <div className="card border-0 shadow-sm p-4 mb-4 info-strip themed-info-card editorial-section-soft appointment-hours-band">
        <div className="split-info-band">
          <div className="editorial-heading-block split-info-band-copy">
            <span className="editorial-kicker">Planning Your Visit</span>
            <h2 className="editorial-title">Clinic Working Hours</h2>
            <p className="editorial-lead">
              Review the schedule, choose the right service, and send the details our team needs to confirm your visit smoothly.
            </p>
            <div className="appointment-hours-guidance">
              <div className="appointment-hours-guidance-item">
                <strong>Choose the right specialty</strong>
                <span>Pick the service first, then request a specific doctor or let the clinic assign one.</span>
              </div>
              <div className="appointment-hours-guidance-item">
                <strong>Share complete patient details</strong>
                <span>Accurate contact, age, address, and visit notes help the team confirm more efficiently.</span>
              </div>
              <div className="appointment-hours-guidance-item">
                <strong>Receive final confirmation</strong>
                <span>Your appointment number is issued only after the admin approves the request.</span>
              </div>
            </div>
          </div>
          <div className="split-info-band-aside">
            <div className="split-info-band-card split-info-band-card-hours">
              <div className="split-hours-item">
                <strong>Weekdays</strong>
                <span>{settings.contactHoursWeekdays}</span>
              </div>
              <div className="split-hours-item">
                <strong>Saturday</strong>
                <span>{settings.contactHoursSaturday}</span>
              </div>
              <div className="split-hours-item">
                <strong>Sunday</strong>
                <span>{settings.contactHoursSunday}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="appointment-hours-copy">
          <p className="mb-0 text-muted">
            For help choosing a suitable slot, contact us at {settings.contactPhone} or {settings.contactEmail}.
          </p>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card border-0 shadow-sm themed-form-shell editorial-band">
        <div className="card-body p-4 p-lg-5">
          <div className="editorial-heading-block">
            <span className="editorial-kicker">Appointment Request</span>
            <h2 className="editorial-title">Tell us the details of your visit.</h2>
            <p className="editorial-lead">
              Share the patient details, choose a service, and either request a specific doctor or let the clinic assign the best available specialist.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label htmlFor="name" className="form-label">Full Name</label>
              <input type="text" className="form-control" id="name" name="name" value={form.name} onChange={handleChange} required />
            </div>

            <div className="col-md-3">
              <label htmlFor="age" className="form-label">Age</label>
              <input type="number" min="0" className="form-control" id="age" name="age" value={form.age} onChange={handleChange} required />
            </div>

            <div className="col-md-3">
              <label htmlFor="gender" className="form-label">Gender</label>
              <select className="form-select" id="gender" name="gender" value={form.gender} onChange={handleChange} required>
                {genderOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="email" className="form-label">Email</label>
              <input type="email" className="form-control" id="email" name="email" value={form.email} onChange={handleChange} required />
            </div>

            <div className="col-md-6">
              <label htmlFor="phone" className="form-label">Phone</label>
              <input type="tel" className="form-control" id="phone" name="phone" value={form.phone} onChange={handleChange} required />
            </div>

            <div className="col-12">
              <label htmlFor="address" className="form-label">Address</label>
              <textarea className="form-control" id="address" name="address" value={form.address} onChange={handleChange} rows="2" required />
            </div>

            <div className="col-md-6">
              <label htmlFor="service" className="form-label">Service</label>
              <select className="form-select" id="service" name="service" value={form.service} onChange={handleChange} required>
                {availableServices.map((service) => (
                  <option key={service._id} value={service.title}>
                    {service.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label htmlFor="date" className="form-label">Preferred Date</label>
              <input type="date" min={new Date().toISOString().split('T')[0]} className="form-control" id="date" name="date" value={form.date} onChange={handleChange} required />
            </div>

            <div className="col-md-6">
              <label htmlFor="time" className="form-label">Preferred Time</label>
              <input type="time" className="form-control" id="time" name="time" value={form.time} onChange={handleChange} required />
            </div>

            <div className="col-12">
              <div className="appointment-doctor-panel">
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                  <div>
                    <label className="form-label mb-1">Doctor Preference</label>
                    <div className="appointment-form-note">
                      Choose a specific doctor for this service, or let the clinic assign the best available specialist.
                    </div>
                  </div>
                  <span className="admin-note">{matchedDoctors.length} doctor option{matchedDoctors.length === 1 ? '' : 's'} available</span>
                </div>

                <div className="appointment-doctor-choice-grid">
                  <button
                    type="button"
                    className={`appointment-doctor-card${form.doctorPreference === 'any' ? ' is-selected' : ''}`}
                    onClick={() => selectDoctor(null)}
                  >
                    <div className="appointment-doctor-card-top">
                      <div className="appointment-doctor-avatar appointment-doctor-avatar-generic">Any</div>
                      <div>
                        <h3>Any Available Doctor</h3>
                        <p>The clinic will assign the most suitable doctor during confirmation.</p>
                      </div>
                    </div>
                  </button>

                  {matchedDoctors.map((doctor) => (
                    <button
                      key={doctor.name}
                      type="button"
                      className={`appointment-doctor-card${form.doctorName === doctor.name && form.doctorPreference === 'specific' ? ' is-selected' : ''}`}
                      onClick={() => selectDoctor(doctor)}
                    >
                      <div className="appointment-doctor-card-top">
                        <img src={doctor.imageUrl} alt={doctor.name} className="appointment-doctor-avatar" />
                        <div>
                          <h3>{doctor.name}</h3>
                          <p>{doctor.specialty}</p>
                        </div>
                      </div>
                      <div className="appointment-doctor-summary">{doctor.summary}</div>
                      <div className="appointment-doctor-meta">{doctor.experience}</div>
                    </button>
                  ))}
                </div>

                {matchedDoctors.length === 0 && (
                  <div className="appointment-form-note mt-3">
                    No doctor profiles are listed for this service yet. The clinic will assign an appropriate specialist during confirmation.
                  </div>
                )}
              </div>
            </div>

            <div className="col-12">
              <label htmlFor="message" className="form-label">Symptoms / Reason for Visit</label>
              <textarea
                className="form-control"
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                rows="4"
                placeholder="Describe the symptoms, health concern, or anything the doctor should know before the appointment."
              />
            </div>

            <div className="col-12">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Appointment Request'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {loading && <LoadingSpinner size="2rem" />}
    </div>
  )
}

export default AppointmentPage
